import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, LogOut, RefreshCw, Search } from "lucide-react";

const STATUSES = ["new", "in_progress", "scheduled", "completed", "cancelled"];
const TINTS = {
  new: "bg-brass/15 text-brass border-brass/30",
  in_progress: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  scheduled: "bg-purple-400/15 text-purple-300 border-purple-400/30",
  completed: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  cancelled: "bg-red-400/15 text-red-300 border-red-400/30",
};

export default function Admin() {
  const { t } = useI18n();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/", { replace: true }); return; }
    if (user.role !== "admin") { navigate("/dashboard", { replace: true }); return; }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const load = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (id, status) => {
    setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${id}`, { status });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      load();
    }
  };

  const filtered = useMemo(() => {
    let arr = leads;
    if (filter !== "all") arr = arr.filter((l) => l.status === filter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((l) =>
        [l.name, l.phone, l.email, l.city, l.service, l.message]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(s))
      );
    }
    return arr;
  }, [leads, filter, q]);

  const counts = useMemo(() => {
    const c = { all: leads.length, new: 0, in_progress: 0, scheduled: 0, completed: 0, cancelled: 0 };
    for (const l of leads) c[l.status] = (c[l.status] || 0) + 1;
    return c;
  }, [leads]);

  if (loading || !user || user.role !== "admin") {
    return <div className="min-h-screen grid place-items-center bg-onyx text-bone/60 text-xs tracking-widest uppercase">Loading…</div>;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-onyx text-bone">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-onyx/95 backdrop-blur-xl border-b border-bone/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4 text-bone/60" />
            <span className="font-display italic-display text-3xl text-brass leading-none">Д</span>
            <div>
              <div className="font-display text-lg leading-none">{t.admin.title}</div>
              <div className="eyebrow text-[0.6rem]">Dzhavala · {user.email}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh" className="text-bone/60 hover:text-brass p-2" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            </button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout" className="text-[0.7rem] tracking-[0.2em] uppercase text-bone/70 hover:text-brass flex items-center gap-2">
              <LogOut className="w-3.5 h-3.5" /> {t.cta.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
        {/* Title */}
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="eyebrow mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-brass" /> {t.admin.leads}
            </div>
            <h1 className="font-display text-4xl lg:text-6xl tracking-tight">
              {t.admin.title}<span className="text-brass">.</span>
            </h1>
          </div>
          <div className="text-right">
            <div className="text-[0.7rem] tracking-[0.2em] uppercase text-taupe">{t.admin.total}</div>
            <div className="font-display text-5xl text-brass">{counts.all}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <button
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            className={`px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase border transition ${filter === "all" ? "border-brass text-brass" : "border-bone/15 text-bone/60 hover:text-bone"}`}
          >
            {t.admin.filter_all} <span className="ml-2 opacity-60">{counts.all}</span>
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-2 text-[0.7rem] tracking-[0.18em] uppercase border transition ${filter === s ? "border-brass text-brass" : "border-bone/15 text-bone/60 hover:text-bone"}`}
            >
              {t.dashboard.status[s]} <span className="ml-2 opacity-60">{counts[s] || 0}</span>
            </button>
          ))}

          <div className="ml-auto flex items-center gap-2 border border-bone/15 px-3 min-w-[260px]">
            <Search className="w-4 h-4 text-taupe" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Пошук…"
              className="flex-1 bg-transparent outline-none py-2 text-sm text-bone placeholder:text-taupe"
            />
          </div>
        </div>

        {error && <div className="mb-4 text-red-400 text-sm">{error}</div>}

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="border border-bone/10 p-12 text-center text-bone/60 text-sm">{t.admin.empty}</div>
        ) : (
          <div className="border border-bone/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[0.65rem] tracking-[0.2em] uppercase text-taupe border-b border-bone/10">
                  <th className="p-4 font-normal">{t.admin.cols.date}</th>
                  <th className="p-4 font-normal">{t.admin.cols.contact}</th>
                  <th className="p-4 font-normal">{t.admin.cols.city}</th>
                  <th className="p-4 font-normal">{t.admin.cols.service}</th>
                  <th className="p-4 font-normal">{t.admin.cols.message}</th>
                  <th className="p-4 font-normal">{t.admin.cols.source}</th>
                  <th className="p-4 font-normal">{t.admin.cols.status}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} data-testid={`admin-lead-${l.id}`} className="border-b border-bone/8 hover:bg-charcoal/40 transition-colors align-top">
                    <td className="p-4 whitespace-nowrap text-bone/70">
                      {new Date(l.created_at).toLocaleDateString("uk-UA")}
                      <div className="text-[0.65rem] text-taupe">{new Date(l.created_at).toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                    <td className="p-4">
                      <div className="font-display text-lg">{l.name}</div>
                      <a href={`tel:${l.phone}`} className="text-bone/80 hover:text-brass">{l.phone}</a>
                      {l.email && <div className="text-xs text-taupe">{l.email}</div>}
                    </td>
                    <td className="p-4 text-bone/80">{l.city || "—"}</td>
                    <td className="p-4 text-bone/80">{l.service || "—"}</td>
                    <td className="p-4 text-bone/70 max-w-xs">
                      <div className="line-clamp-3">{l.message || "—"}</div>
                    </td>
                    <td className="p-4 text-[0.65rem] tracking-[0.18em] uppercase text-taupe">{l.source || "—"}</td>
                    <td className="p-4">
                      <select
                        data-testid={`status-${l.id}`}
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className={`bg-onyx border px-2 py-1.5 text-[0.65rem] tracking-[0.2em] uppercase rounded-full ${TINTS[l.status] || TINTS.new}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-onyx text-bone">{t.dashboard.status[s]}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
